import {
  closestCorners,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { useEffect, useState } from 'react'

import KanbanColumn from './kanban-column'
import {
  DataTypeRecord,
  KanbanColumnProps,
  KanbanItemProps,
  KanbanItemRequiredFields,
  KanbanProps
} from './types'
import { announcements, getContainer, getIndex, getItem } from './utils'

const getInitialData = <T,>(columns: KanbanColumnProps[], data: KanbanItemProps<T>[]) =>
  columns.map((column) => {
    return {
      ...column,
      data: data
        .filter((item) => item.columnId === column.id)
        .map((item) => ({ ...item, isDragging: false }))
    }
  })

const getStructuredItems = <T extends KanbanItemRequiredFields>(items: DataTypeRecord<T>[]): T[] =>
  items
    .map((column) => column.data.map((item) => ({ id: item.id, columnId: column.id }) as T))
    .flat()

/**
 * Usage
 *
 * ```tsx
 * import {
 *   Kanban,
 *   KanbanCardComponentExtraProps,
 *   KanbanItemRequiredFields,
 * } from "@repo/ui/kanban";
 * import { PaperClickIcon, PaperTimeIcon, PaperWriteIcon } from "@repo/ui/icons";
 *
 * interface Props extends KanbanItemRequiredFields {
 *   title: string;
 * }
 * const TaskCard = ({ title }: Props & KanbanCardComponentExtraProps) => {
 *   return <div>{title}</div>;
 * };
 *
 * export default function HomePage() {
 *   const columns = [
 *     { id: "1", title: "Column 1", icon: PaperClickIcon },
 *     { id: "2", title: "Column 2", icon: PaperTimeIcon },
 *     { id: "3", title: "Column 3", icon: PaperWriteIcon },
 *   ];
 *
 *   const data = [
 *     { id: "11", columnId: "1", title: "Task 1" },
 *     { id: "22", columnId: "1", title: "Task 2" },
 *     { id: "33", columnId: "2", title: "Task 3" },
 *     { id: "44", columnId: "2", title: "Task 4" },
 *     { id: "55", columnId: "3", title: "Task 5" },
 *     { id: "66", columnId: "3", title: "Task 6" },
 *   ];
 *
 *   return (
 *     <Kanban<Props>
 *       columns={columns}
 *       data={data}
 *       gap={14}
 *       card={TaskCard}
 *       onUpdate={() => {
 *         return true;
 *       }}
 *     />
 *   );
 * }
 *
 * ```
 */
const Kanban = <T extends KanbanItemRequiredFields>({
  columns,
  data,
  gap = 14,
  card: Card,
  disableColumns,
  onUpdate,
  onDragStart,
  onDragEnd
}: KanbanProps<T> & {
  disableColumns?: UniqueIdentifier[]
  onDragStart?: (event: DragStartEvent) => void
  onDragEnd?: (event: DragEndEvent) => void
}) => {
  const [oldItems, setOldItems] = useState<DataTypeRecord<T>[]>(getInitialData(columns, data))
  const [items, setItems] = useState<DataTypeRecord<T>[]>(getInitialData(columns, data))

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)
  const [item, setItem] = useState<UniqueIdentifier | null>(null)

  const [isDisabled, setIsDisabled] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-dnd-disabled') {
          const isDndDisabled = (mutation.target as HTMLElement).getAttribute('data-dnd-disabled')
          setIsDisabled(isDndDisabled === 'true')
        }
      })
    })

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-dnd-disabled']
    })

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    const callback = async () => {
      if (activeId === null) {
        const newItem = getStructuredItems(items).find((column) => column.id === item)
        const oldItem = getStructuredItems(oldItems).find((column) => column.id === item)
        if (!newItem || !oldItem) return
        const isUpdated = await onUpdate({
          newState: getStructuredItems(items),
          oldState: getStructuredItems(oldItems),
          newItem,
          oldItem
        })
        if (!isUpdated) setItems(oldItems)
      }
    }
    callback()
  }, [activeId])

  function handleDragStart(event: DragStartEvent) {
    const { active } = event
    const { id } = active
    setActiveId(id)
    setItem(id)
    setOldItems(items)
    onDragStart?.(event)
  }

  function handleDragOver(event: DragOverEvent) {
    const { active, over } = event
    if (!over) return

    /** get ids of cards */
    const activeId = active.id
    const overId = over.id

    setItems((prevItems) => {
      const activeItem = getItem(prevItems, activeId)
      const overItem = getItem(prevItems, overId)

      if (!activeItem || !overItem) return prevItems

      const activeContainer = getContainer<KanbanItemProps<T>>(prevItems, activeItem.columnId)
      const overContainer = getContainer<KanbanItemProps<T>>(prevItems, overItem.columnId)

      if (!activeContainer || !overContainer) return prevItems

      /** move item depending on index and column */
      if (activeContainer.id !== overContainer.id) {
        const overIndex = getIndex<KanbanItemProps<T>>(overContainer.data, overId)

        return prevItems.map((column) => {
          if (column.id === activeContainer.id) {
            return {
              ...column,
              data: column.data.filter((item) => item.id !== activeItem.id)
            }
          }
          if (column.id === overContainer.id) {
            const updatedActiveItem = {
              ...activeItem,
              columnId: overContainer.id
            }
            const newData = [...overContainer.data]
            newData.splice(overIndex, 0, updatedActiveItem)
            return {
              ...column,
              data: newData
            }
          }

          return column
        })
      }

      return prevItems
    })
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over) return
    onDragEnd?.(event)

    /** get ids */
    const activeId = active.id
    const overId = over.id

    setItems((prevItems) => {
      const activeItem = getItem<T>(prevItems, activeId)
      const overItem = getItem<T>(prevItems, overId)

      if (!activeItem) return prevItems

      const activeContainer = getContainer<KanbanItemProps<T>>(prevItems, activeItem.columnId)
      const overContainer = getContainer<KanbanItemProps<T>>(
        prevItems,
        overItem ? overItem.columnId : overId
      )

      if (!activeContainer || !overContainer) return prevItems

      const activeIndex = getIndex<KanbanItemProps<T>>(activeContainer.data, activeId)
      const overIndex = getIndex<KanbanItemProps<T>>(overContainer.data, overId)

      let newState = prevItems

      /** move item depending on index and column */
      if (activeContainer.id !== overContainer.id) {
        newState = prevItems.map((column) => {
          if (column.id === activeContainer.id) {
            return {
              ...column,
              data: column.data.filter((item) => item.id !== activeItem.id)
            }
          }
          if (column.id === overContainer.id) {
            const updatedActiveItem = {
              ...activeItem,
              columnId: overContainer.id
            }
            return {
              ...column,
              data: arrayMove([...overContainer.data, updatedActiveItem], overIndex, overIndex)
            }
          }
          return column
        })
      } else if (activeIndex !== overIndex) {
        newState = prevItems.map((column) => {
          if (column.id === activeContainer.id) {
            return {
              ...column,
              data: arrayMove(column.data, activeIndex, overIndex)
            }
          }
          return column
        })
      }
      return newState
    })

    setActiveId(null)
  }

  useEffect(() => {
    setItems(getInitialData(columns, data))
  }, [data])

  return (
    <div className={'flex flex-row gap-6'}>
      <DndContext
        accessibility={{ announcements }}
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        {items.map((column) => (
          <KanbanColumn<T>
            {...column}
            key={column.id}
            card={Card}
            gap={gap}
            disabled={
              isDisabled ||
              column.isDropDisabled ||
              (disableColumns
                ? column.id === disableColumns.find((id) => id === column.id)
                : false)
            }
          />
        ))}

        <DragOverlay zIndex={10}>
          {activeId ? (
            <div className={'opacity-50 '}>
              <Card
                {...(getItem(items, activeId) as T)}
                isDragging={true}
                id={activeId}
                columnId={getItem(items, activeId)?.columnId}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  )
}

export * from './types'

export default Kanban
